import { Platform } from 'react-native'
import * as LocalAuthentication from 'expo-local-authentication'

// Display name for whichever biometric the device supports. We pick a
// per-platform name that reads naturally in user-facing copy:
//
// - iOS: Apple's brand names ("Face ID", "Touch ID") — required by HIG.
// - Android: the everyday name of the modality ("fingerprint", "face unlock"),
//   so prompts read like "Use your fingerprint" rather than the techy
//   "Use Biometrics".
//
// Strings are intentionally lowercase for non-branded labels so they slot
// into mid-sentence copy ("Sign in faster with your fingerprint"). Use
// `capitalizeBiometricLabel` if you need them at the start of a sentence.
export type BiometricLabel =
  | 'Face ID'
  | 'Touch ID'
  | 'fingerprint'
  | 'face unlock'
  | 'iris'
  | 'biometric unlock'

export interface BiometricAvailability {
  hasHardware: boolean
  isEnrolled: boolean
  available: boolean
  label: BiometricLabel
}

const isWeb = Platform.OS === 'web'

const DEFAULT_LABEL: BiometricLabel = 'biometric unlock'

export const getBiometricAvailability = async (): Promise<BiometricAvailability> => {
  if (isWeb) {
    return { hasHardware: false, isEnrolled: false, available: false, label: DEFAULT_LABEL }
  }

  try {
    const [hasHardware, isEnrolled, supportedTypes] = await Promise.all([
      LocalAuthentication.hasHardwareAsync(),
      LocalAuthentication.isEnrolledAsync(),
      LocalAuthentication.supportedAuthenticationTypesAsync(),
    ])

    let label: BiometricLabel = DEFAULT_LABEL
    if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
      label = Platform.OS === 'ios' ? 'Face ID' : 'face unlock'
    } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
      label = Platform.OS === 'ios' ? 'Touch ID' : 'fingerprint'
    } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.IRIS)) {
      label = 'iris'
    }

    return {
      hasHardware,
      isEnrolled,
      available: hasHardware && isEnrolled,
      label,
    }
  } catch {
    return { hasHardware: false, isEnrolled: false, available: false, label: DEFAULT_LABEL }
  }
}

/**
 * Returns the label capitalised for the start of a sentence, leaving
 * already-branded names ("Face ID", "Touch ID") untouched.
 *   "Face ID" → "Face ID"
 *   "fingerprint" → "Fingerprint"
 *   "face unlock" → "Face unlock"
 */
export const capitalizeBiometricLabel = (label: BiometricLabel): string => {
  if (label === 'Face ID' || label === 'Touch ID') return label
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/**
 * Friendly button copy. Uses Apple's brand names on iOS and natural
 * "your fingerprint" / "your face" phrasing elsewhere.
 *   "Face ID" → "Use Face ID"
 *   "fingerprint" → "Use your fingerprint"
 *   "face unlock" → "Use your face"
 *   "iris" → "Use your iris"
 *   "biometric unlock" → "Quick sign-in"
 */
export const getBiometricButtonText = (label: BiometricLabel): string => {
  if (label === 'Face ID' || label === 'Touch ID') return `Use ${label}`
  if (label === 'biometric unlock') return 'Quick sign-in'
  if (label === 'face unlock') return 'Use your face'
  return `Use your ${label}`
}

/**
 * Inline phrase, suitable after prepositions like "with" or "by".
 *   "Face ID" → "Face ID"
 *   "fingerprint" → "your fingerprint"
 *   "face unlock" → "your face"
 *   "iris" → "your iris"
 *   "biometric unlock" → "biometric unlock"
 */
export const getBiometricInlinePhrase = (label: BiometricLabel): string => {
  if (label === 'Face ID' || label === 'Touch ID') return label
  if (label === 'biometric unlock') return label
  if (label === 'face unlock') return 'your face'
  return `your ${label}`
}

export const isBiometricAvailable = async (): Promise<boolean> => {
  const availability = await getBiometricAvailability()
  return availability.available
}

export const getBiometricLabel = async (): Promise<BiometricLabel> => {
  const availability = await getBiometricAvailability()
  return availability.label
}

export interface BiometricAuthResult {
  success: boolean
  error?: LocalAuthentication.LocalAuthenticationError
  cancelled?: boolean
}

export const authenticate = async (reason: string): Promise<BiometricAuthResult> => {
  if (isWeb) {
    return { success: false, error: 'not_available' }
  }

  const result = await LocalAuthentication.authenticateAsync({
    promptMessage: reason,
    fallbackLabel: 'Use passcode',
    cancelLabel: 'Cancel',
    disableDeviceFallback: false,
  })

  if (result.success) {
    return { success: true }
  }

  const cancelled = result.error === 'user_cancel' || result.error === 'system_cancel' || result.error === 'app_cancel'

  return {
    success: false,
    error: result.error,
    cancelled,
  }
}
